"use client";

import * as React from "react";
import { File as FileIcon, UploadCloud, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "./button";

// CargoFL ShadCN Library — FileUpload (drag-and-drop + click, presentational).
export interface FileUploadProps {
  documentType: string;
  invoiceId?: number;
  onUploadComplete?: () => void;
  multiple?: boolean;
  /**
   * Hands the picked files to the caller and keeps none of them. Supply this when
   * the list of files lives on a server: the caller renders what was stored, and
   * two lists showing the same names - one local, one real - never appear.
   */
  onFiles?: (files: File[]) => void;
  accept?: string;
}

function FileUpload({
  documentType,
  invoiceId,
  onUploadComplete,
  multiple = false,
  onFiles,
  accept,
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [files, setFiles] = React.useState<File[]>([]);

  const handleFiles = React.useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const incoming = Array.from(fileList);
      if (onFiles) {
        onFiles(incoming);
        return;
      }
      setFiles((prev) => (multiple ? [...prev, ...incoming] : incoming));
      void invoiceId;
      onUploadComplete?.();
    },
    [multiple, onFiles, onUploadComplete, invoiceId]
  );

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragging(false);
          handleFiles(event.dataTransfer.files);
        }}
        className={cn(
          "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-input bg-muted/30 px-6 py-10 text-center transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          isDragging && "border-primary bg-accent"
        )}
      >
        <UploadCloud
          className={cn(
            "size-8 text-muted-foreground",
            isDragging && "text-primary"
          )}
        />
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">
            Upload {documentType}
          </p>
          <p className="text-xs text-muted-foreground">
            Drag and drop {multiple ? "files" : "a file"} here, or click to
            browse
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          className="hidden"
          onChange={(event) => {
            handleFiles(event.target.files);
            // Clear it, or picking the same file twice fires no change event at
            // all - which is exactly what someone does after a failed upload.
            event.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <ul className="flex flex-col gap-2">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-2 rounded-md border border-border bg-card px-3 py-2 text-card-foreground"
            >
              <div className="flex min-w-0 items-center gap-2">
                <FileIcon className="size-4 shrink-0 text-muted-foreground" />
                <span className="truncate text-sm">{file.name}</span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                onClick={() => removeFile(index)}
              >
                <X className="size-4" />
                <span className="sr-only">Remove {file.name}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
FileUpload.displayName = "FileUpload";

export { FileUpload };
